import {
  computeIndicators,
  formatDate,
  formatDateTime,
  type Project,
  type ProjectTimesheets,
  routes,
  Scenarios,
  todayIso,
} from '@pmai/contracts';
import { Button, DetailGrid, PageHeader, ProgressBar, Spinner } from '@pmai/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { NotesPanel } from '../../entities/notes/notes-panel';
import { ApiError } from '../../shared/api/client';
import { useApiQuery } from '../../shared/api/hooks';
import { Can, usePermissions } from '../../shared/auth/session';
import { CardSection } from '../../shared/card-section';
import { ExternalLink } from '../../shared/external-link';
import { useErrorText } from '../../shared/i18n';
import { NotFoundPage } from '../../shared/system-pages';
import { useDocumentTitle } from '../../shared/use-document-title';
import { JiraKeyLinks } from '../../entities/jira/jira-links';
import { IndicatorStrip } from '../../entities/projects/indicator-strip';
import { PriorityBadge, ProjectStatusBadge } from '../../entities/projects/project-badges';
import { EffectiveHoursSection } from './effective-hours-section';
import { ProjectDialog } from './project-dialog';
import { ProjectTasksSection } from './project-tasks';
import { ProjectTechnologiesSection } from './project-technologies';
import { extendToStart } from './timesheet/sheet-edit';
import { FinancialSummarySection, SheetSection } from './timesheet/timesheet-sections';
import { useTimesheetEditor } from './timesheet/use-timesheet-editor';

/** Project card: waits for the project and its timesheets, then shows {@link ProjectCard}. */
export function ProjectPage() {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const { id = '' } = useParams();
  const project = useApiQuery(routes.projects.get, { params: { id } }, { retry: false });
  const timesheets = useApiQuery(
    routes.timesheets.ofProject,
    { params: { id } },
    { retry: false, enabled: project.isSuccess },
  );
  useDocumentTitle(project.data?.name);

  const error = project.error ?? timesheets.error;
  if (error) {
    const missing = error instanceof ApiError && error.code === 'NOT_FOUND';
    return <NotFoundPage title={missing ? t('projects.notFound') : errorText(error)} />;
  }
  // The editor starts from fresh sheets only — never from an earlier visit's cache.
  if (!project.data || !timesheets.data || !timesheets.isFetchedAfterMount)
    return <Spinner label={t('common.loading')} />;
  return <ProjectCard key={id} project={project.data} timesheets={timesheets.data} />;
}

/**
 * Project card — indicators (FR-PRJ-017…023), FR-PRJ-012…016, technologies (FR-TECH-007…010),
 * financial summary, Plan and Fact (FR-TS-001…019), notes (FR-PRJ-026…032), tasks
 * (FR-TASK-001…009).
 */
function ProjectCard({ project: p, timesheets }: { project: Project; timesheets: ProjectTimesheets }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const editor = useTimesheetEditor(p.id, timesheets);
  const finance = usePermissions().includes('finance.view');
  const indicators = computeIndicators(p, editor.sheets.plan, editor.sheets.fact, todayIso());
  // FR-TS-016: an earlier start date adds months in front of both sheets.
  const onSaved = (saved: Project) => {
    const [year, month] = saved.startDate.split('-').map(Number) as [number, number];
    for (const scenario of Scenarios.codes) {
      const first = editor.sheets[scenario].months[0]!;
      if (year * 12 + month < first.year * 12 + first.month)
        editor.change(scenario, (s) => extendToStart(s, { year, month }));
    }
  };

  const f = (field: string) => t(`projects.fields.${field}`);
  return (
    <>
      <PageHeader
        title={p.name}
        back={{ to: '/', label: t('projects.back') }}
        actions={
          <Can perm="projects.edit">
            <Button variant="secondary" icon="pencil" onClick={() => setEditing(true)}>
              {t('common.edit')}
            </Button>
          </Can>
        }
      />
      <div className="flex flex-col gap-5">
        <IndicatorStrip indicators={indicators} financialManagement={p.financialManagement} />
        <CardSection id="project.basic" title={t('projects.sections.basic')}>
          <DetailGrid
            items={[
              {
                label: f('customer'),
                value: (
                  <Link to={`/customers/${p.customerId}`} className="text-brand-700 hover:underline">
                    {p.customerName}
                  </Link>
                ),
              },
              { label: f('status'), value: <ProjectStatusBadge status={p.status} /> },
              { label: f('priority'), value: <PriorityBadge priority={p.priority} /> },
              { label: f('startDate'), value: formatDate(p.startDate) },
              { label: f('plannedFinish'), value: formatDate(p.plannedFinish) },
              { label: f('actualFinish'), value: formatDate(p.actualFinish) },
              { label: f('createdAt'), value: formatDateTime(p.createdAt) },
              { label: f('updatedAt'), value: formatDateTime(p.updatedAt) },
              { label: f('completion'), value: <ProgressBar value={p.completion} label={f('completion')} /> },
              {
                label: f('financial'),
                value: p.financialManagement
                  ? `✅ ${t('projects.financialOn')}`
                  : `❌ ${t('projects.financialOff')}`,
              },
              {
                label: f('charter'),
                value: <ExternalLink href={p.charterUrl} label={`📄 ${t('projects.openCharter')}`} />,
              },
              { label: f('jiraProject'), value: <JiraKeyLinks keys={p.jiraProjectKeys} /> },
              { label: f('jiraEpic'), value: <JiraKeyLinks keys={p.jiraEpicKeys} /> },
            ]}
          />
        </CardSection>
        <ProjectTechnologiesSection projectId={p.id} />
        {finance && <FinancialSummarySection editor={editor} financialManagement={p.financialManagement} />}
        <SheetSection scenario="plan" editor={editor} financialManagement={p.financialManagement} />
        <SheetSection scenario="fact" editor={editor} financialManagement={p.financialManagement} />
        <EffectiveHoursSection projectId={p.id} />
        <CardSection id="project.notes" title={t('projects.sections.notes')}>
          <NotesPanel owner={{ kind: 'project', id: p.id }} />
        </CardSection>
        <ProjectTasksSection projectId={p.id} />
      </div>
      <ProjectDialog open={editing} project={p} onClose={() => setEditing(false)} onSaved={onSaved} />
    </>
  );
}
